// doctor-frontend/src/pages/citizen/CitizenMedicalHistory.js
// Canonical adaptive History editor for the CMFM Visit workflow.
// Preserves existing ribbon save/navigation and API fallbacks while storing
// common and pathway-specific History in one Visit record.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

import EncounterHeader from '../../components/EncounterHeader';
import useVisitReadOnly from '../../useVisitReadOnly';
import http from '../../services/http';
import { derivePrimaryClinicalContext, formatChiefComplaintSummary } from '../../clinical/visitContextEngine';
import { getSectionModulePlan, moduleLabels } from '../../clinical/visitSectionModules';

function getTenantBase() {
  try {
    const match = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    return match ? match[0] : '';
  } catch {
    return '';
  }
}

function tenantUrl(path) {
  const base = getTenantBase();
  const value = String(path || '');
  const clean = value.startsWith('/') ? value : `/${value}`;
  return `${base}${clean}`.replace(/\/{2,}/g, '/');
}

const NONE_OPTION = 'None / कोई नहीं';

const pastIllnessOptions = [
  NONE_OPTION,
  'Diabetes Mellitus / मधुमेह',
  'Hypertension / उच्च रक्तचाप',
  'Both Diabetes & Hypertension / दोनों मधुमेह व उच्च रक्तचाप',
  'CAD / हृदय रोग',
  'Tuberculosis / क्षय रोग',
  'COPD / सीओपीडी',
  'Asthma / अस्थमा',
  'Anemia / एनीमिया',
  'Thyroid disorder / थायरॉयड विकार',
  'Chronic kidney disease / क्रोनिक किडनी रोग',
  'Epilepsy / मिर्गी',
];

const HOPI_ONSET_OPTIONS = ['', 'Sudden', 'Gradual', 'Not sure'];
const HOPI_COURSE_OPTIONS = ['', 'Improving', 'Static', 'Progressive', 'Intermittent', 'Recurrent', 'Fluctuating'];
const HOPI_SEVERITY_OPTIONS = ['', 'Mild', 'Moderate', 'Severe', 'Not graded'];
const FACILITY_TYPE_OPTIONS = ['', 'Government', 'Private', 'Charitable / Trust', 'Other'];
const INFORMATION_SOURCE_OPTIONS = [
  '',
  'Discharge summary / hospital document reviewed',
  'Patient / caregiver reported',
  'Other medical record reviewed',
  'Other',
];
const ICU_OPTIONS = ['', 'No', 'Yes', 'Not known'];

function complaintDisplayName(item, index) {
  if (isPlainObject(item)) {
    return stringValue(
      item.complaint ||
      item.label ||
      item.name ||
      item.text ||
      item.title ||
      item.value ||
      item.chiefComplaint
    ) || `Chief complaint ${index + 1}`;
  }
  return stringValue(item) || `Chief complaint ${index + 1}`;
}

function complaintDurationText(item) {
  if (!isPlainObject(item)) return '';
  const value = stringValue(item.duration ?? item.durationValue ?? item.since ?? '');
  const unit = stringValue(item.durationUnit ?? item.unit ?? item.durationUnits ?? '');
  return [value, unit].filter(Boolean).join(' ');
}

function complaintStorageKey(item, index) {
  if (isPlainObject(item)) {
    const explicit = stringValue(item.id || item._id || item.key || item.code);
    if (explicit) return explicit;
  }
  const label = complaintDisplayName(item, index)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${label || 'complaint'}-${index + 1}`;
}

function emptyHospitalisation() {
  return {
    admissionDate: '',
    dischargeDate: '',
    facilityType: '',
    facilityName: '',
    location: '',
    reason: '',
    dischargeDiagnosis: '',
    procedures: '',
    icuAdmission: '',
    importantInvestigations: '',
    dischargeMedicines: '',
    outcome: '',
    informationSource: '',
    notes: '',
  };
}

const contextualInitialState = {
  complaintHopi: {},
  hospitalisations: [],
  newborn: {
    birthHistory: '',
    gestationalAge: '',
    deliveryMode: '',
    resuscitationApgar: '',
    feeding: '',
    jaundice: '',
    urineStool: '',
    dangerSigns: '',
  },
  paediatric: {
    caregiverConcern: '',
    symptomDuration: '',
    feedingHistory: '',
    breastfeeding: '',
    complementaryFeeding: '',
    growthConcern: '',
    development: '',
    imnciDangerSigns: '',
  },
  adolescent: {
    privacyConsent: '',
    homeEducation: '',
    activitiesPeers: '',
    nutritionBodyImage: '',
    substanceRisk: '',
    sexualReproductiveHealth: '',
    moodSelfHarm: '',
    safetyViolence: '',
  },
  anc: {
    gravidityParity: '',
    lmpEdd: '',
    gestationalAge: '',
    previousObstetricHistory: '',
    currentPregnancyConcerns: '',
    fetalMovement: '',
    bleedingLeakage: '',
    supplementsImmunization: '',
    dangerSigns: '',
  },
  pnc: {
    deliveryDatePlace: '',
    deliveryModeOutcome: '',
    postpartumDay: '',
    maternalComplaints: '',
    bleedingFever: '',
    breastFeedingProblems: '',
    contraceptionCounselling: '',
    newbornConcerns: '',
    dangerSigns: '',
  },
  ncd: {
    knownConditions: '',
    durationControl: '',
    homeReadings: '',
    treatmentAdherence: '',
    complicationsSymptoms: '',
    hypoglycemiaEpisodes: '',
    lastReviewResults: '',
    lifestyleBarriers: '',
  },
  geriatric: {
    cognition: '',
    visionHearing: '',
    mobilityFalls: '',
    continence: '',
    polypharmacy: '',
    nutrition: '',
    socialSupport: '',
    caregiverConcerns: '',
  },
  occupational: {
    // Brief occupational enquiry shown when work/exposure may matter.
    currentWork: '',
    actualTasks: '',
    importantExposures: '',
    symptomWorkRelation: '',
    coworkerSymptoms: '',

    // Focused occupational history retained for exposure-linked presentations.
    occupationIndustry: '',
    duration: '',
    hazards: '',
    ppe: '',
    accidents: '',
    screening: '',
    previousRelevantWork: '',
    functionalWorkImpact: '',
    compensationReferral: '',
  },
  emergency: {
    onsetTimeline: '',
    dangerSigns: '',
    firstAid: '',
    prehospitalTreatment: '',
    drugsAllergies: '',
    lastMeal: '',
    eventsMechanism: '',
    timeCriticalNotes: '',
  },
};

const initialState = {
  hopi: '',
  pastIllnesses: [],
  personalHistory: '',
  familyHistory: '',
  menstrualHistory: '',
  allergies: '',
  surgeries: '',
  currentMedications: '',
  lastDoseTime: '',
  medicationAdherence: '',
  adverseDrugReactions: '',
  immunizationHistory: '',
  recentTravel: '',
  occupationalExposures: '',
  environmentalExposures: '',
  tobaccoPackYears: '',
  alcoholUnitsPerWeek: '',
  physicalActivityMinsPerWeek: '',
  dietNotes: '',
  sleepIssues: '',
  substanceUse: '',
  mentalHealthScreen: '',
  functionalStatus: '',
  fallsPastYear: '',
  contextual: contextualInitialState,
};

const contextSections = {
  newborn: {
    title: 'Newborn / Young Infant History',
    description:
      'Birth, feeding and danger-sign history is stored inside this Visit and reused by newborn-focused views.',
    fields: [
      ['birthHistory', 'Birth history (place, attendant, complications)'],
      ['gestationalAge', 'Gestational age / prematurity history'],
      ['deliveryMode', 'Mode of delivery and immediate outcome'],
      ['resuscitationApgar', 'Resuscitation / APGAR / cry at birth'],
      ['feeding', 'Feeding history (initiation, frequency, attachment, poor feeding)'],
      ['jaundice', 'Jaundice (onset, progression, palms/soles)'],
      ['urineStool', 'Urine and stool passage'],
      ['dangerSigns', 'Newborn danger signs'],
    ],
  },
  paediatric: {
    title: 'Child / IMNCI History',
    description:
      'Age-appropriate child history is entered once and reused by Under-5, nutrition and child-care views.',
    fields: [
      ['caregiverConcern', 'Caregiver’s main concern'],
      ['symptomDuration', 'Duration and progression of symptoms'],
      ['feedingHistory', 'Current feeding and appetite'],
      ['breastfeeding', 'Breastfeeding history, where applicable'],
      ['complementaryFeeding', 'Complementary feeding / dietary diversity'],
      ['growthConcern', 'Growth or weight concern'],
      ['development', 'Developmental milestones / regression'],
      ['imnciDangerSigns', 'General danger signs / IMNCI concerns'],
    ],
  },
  adolescent: {
    title: 'Adolescent History',
    description:
      'Document sensitive information after privacy, assent/consent and safeguarding requirements are addressed.',
    fields: [
      ['privacyConsent', 'Privacy, assent/consent and accompanying person'],
      ['homeEducation', 'Home, family, education and attendance'],
      ['activitiesPeers', 'Activities, peers, screen use and social support'],
      ['nutritionBodyImage', 'Nutrition, physical activity and body-image concerns'],
      ['substanceRisk', 'Tobacco, alcohol or other substance risk'],
      ['sexualReproductiveHealth', 'Sexual and reproductive health, when clinically relevant'],
      ['moodSelfHarm', 'Mood, anxiety, self-harm or suicidal risk'],
      ['safetyViolence', 'Safety, bullying, abuse or violence concerns'],
    ],
  },
  anc: {
    title: 'Antenatal History',
    description: 'This block appears only when the Visit is identified as ANC/pregnancy-related.',
    fields: [
      ['gravidityParity', 'Gravidity, parity, abortions and living children'],
      ['lmpEdd', 'LMP and EDD'],
      ['gestationalAge', 'Gestational age and dating method'],
      ['previousObstetricHistory', 'Previous obstetric outcomes / complications'],
      ['currentPregnancyConcerns', 'Current pregnancy complaints and course'],
      ['fetalMovement', 'Fetal movement, where applicable'],
      ['bleedingLeakage', 'Bleeding, leaking or labour symptoms'],
      ['supplementsImmunization', 'IFA/calcium adherence and immunization'],
      ['dangerSigns', 'Antenatal danger signs'],
    ],
  },
  pnc: {
    title: 'Postnatal History',
    description: 'Maternal and newborn postnatal concerns remain linked to the same clinical Visit.',
    fields: [
      ['deliveryDatePlace', 'Date and place of delivery'],
      ['deliveryModeOutcome', 'Mode of delivery and maternal/newborn outcome'],
      ['postpartumDay', 'Postpartum day / period since delivery'],
      ['maternalComplaints', 'Current maternal complaints'],
      ['bleedingFever', 'Bleeding, fever, pain or urinary/bowel concern'],
      ['breastFeedingProblems', 'Breast and breastfeeding concerns'],
      ['contraceptionCounselling', 'Postpartum contraception discussion'],
      ['newbornConcerns', 'Newborn feeding, jaundice or other concerns'],
      ['dangerSigns', 'Postnatal danger signs'],
    ],
  },
  ncd: {
    title: 'NCD / Chronic-care History',
    description:
      'Chronic disease control and adherence details remain with the Visit rather than in a separate NCD record.',
    fields: [
      ['knownConditions', 'Known NCDs and date/year of diagnosis'],
      ['durationControl', 'Duration, control and recent symptoms'],
      ['homeReadings', 'Home BP/glucose/other monitoring'],
      ['treatmentAdherence', 'Treatment adherence and barriers'],
      ['complicationsSymptoms', 'Complication symptoms / target-organ concerns'],
      ['hypoglycemiaEpisodes', 'Hypoglycaemia or treatment-related events'],
      ['lastReviewResults', 'Last review, investigations and medication change'],
      ['lifestyleBarriers', 'Diet, activity, tobacco/alcohol and social barriers'],
    ],
  },
  geriatric: {
    title: 'Geriatric History',
    description:
      'Functional and social history entered here can be reused by the geriatric-focused clinical view.',
    fields: [
      ['cognition', 'Memory, cognition, delirium or behaviour concerns'],
      ['visionHearing', 'Vision and hearing concerns'],
      ['mobilityFalls', 'Mobility, gait, falls and assistive devices'],
      ['continence', 'Urinary or faecal continence'],
      ['polypharmacy', 'Polypharmacy, self-medication and medicine organization'],
      ['nutrition', 'Appetite, weight loss, dentition and nutrition'],
      ['socialSupport', 'Living arrangement, finances and social support'],
      ['caregiverConcerns', 'Caregiver burden and care concerns'],
    ],
  },
  occupational: {
    title: 'Occupational History',
    description:
      'Occupation-related details remain part of the Visit and may be displayed in Occupational Health views.',
    fields: [
      ['currentWork', 'Current work / livelihood activity (include more than one if relevant)'],
      ['actualTasks', 'What work is actually done during a usual day / shift'],
      ['importantExposures', 'Important dust, fumes, chemicals, biological agents, noise, heat, strain or other exposures'],
      ['symptomWorkRelation', 'Do symptoms worsen during work or improve away from work?'],
      ['coworkerSymptoms', 'Similar symptoms among co-workers with similar tasks / exposures'],
      ['occupationIndustry', 'Work setting / industry / employment arrangement'],
      ['duration', 'Duration, hours, shift pattern and exposure pattern'],
      ['hazards', 'Focused task-specific physical, chemical, biological, ergonomic or psychosocial hazards'],
      ['ppe', 'Available controls / PPE and regular use'],
      ['previousRelevantWork', 'Previous relevant jobs, tasks or exposures'],
      ['accidents', 'Occupational injury, spill, accident or near-miss history'],
      ['screening', 'Previous occupational screening / surveillance'],
      ['functionalWorkImpact', 'Effect of illness on work and usual tasks'],
      ['compensationReferral', 'Referral, work modification / restriction or other occupational-health plan'],
    ],
  },
  emergency: {
    title: 'Emergency / Time-critical History',
    description:
      'Do not delay emergency care to complete this form. Record available information while stabilization proceeds.',
    fields: [
      ['onsetTimeline', 'Exact onset and timeline'],
      ['dangerSigns', 'Immediate danger signs / instability'],
      ['firstAid', 'First aid or immediate measures already taken'],
      ['prehospitalTreatment', 'Pre-hospital treatment and response'],
      ['drugsAllergies', 'Critical medicines, anticoagulants and allergies'],
      ['lastMeal', 'Last meal / oral intake, when relevant'],
      ['eventsMechanism', 'Events, injury mechanism or precipitating factor'],
      ['timeCriticalNotes', 'Other time-critical information'],
    ],
  },
};

const commonSections = [
  {
    key: 'personal',
    title: 'Personal, Family and Reproductive History',
    fields: [
      ['personalHistory', 'Personal history and relevant daily routine'],
      ['familyHistory', 'Family history'],
      ['menstrualHistory', 'Menstrual / relevant reproductive history', 'menstrual'],
    ],
  },
  {
    key: 'allergyProcedure',
    title: 'Allergies and Previous Procedures',
    fields: [
      ['allergies', 'Allergies (drug, food and other)'],
      ['surgeries', 'Past surgeries / procedures'],
    ],
  },
  {
    key: 'medication',
    title: 'Medication History',
    visibility: 'medication',
    fields: [
      ['currentMedications', 'Current medicines (name, dose and frequency)'],
      ['lastDoseTime', 'Last dose time', 'half'],
      ['medicationAdherence', 'Adherence and reason for missed/stopped treatment', 'half'],
      ['adverseDrugReactions', 'Adverse drug reactions'],
    ],
  },
  {
    key: 'immunization',
    title: 'Immunization History',
    visibility: 'immunization',
    fields: [
      ['immunizationHistory', 'Vaccines received, due vaccines and dates where known'],
    ],
  },
  {
    key: 'exposure',
    title: 'Travel and Exposure History',
    visibility: 'exposure',
    fields: [
      ['recentTravel', 'Recent travel and relevant contact history'],
      ['occupationalExposures', 'Occupational exposures (silica, chemicals, noise, heat and others)'],
      ['environmentalExposures', 'Environmental exposures (smoke, water, animals and others)'],
    ],
  },
  {
    key: 'lifestyle',
    title: 'Lifestyle and Behavioural History',
    visibility: 'lifestyle',
    fields: [
      ['tobaccoPackYears', 'Tobacco use / pack-years', 'substanceHalf'],
      ['alcoholUnitsPerWeek', 'Alcohol use / units per week', 'substanceHalf'],
      ['physicalActivityMinsPerWeek', 'Physical activity (minutes/week)', 'half'],
      ['sleepIssues', 'Sleep issues (snoring, apnoea or insomnia)', 'half'],
      ['dietNotes', 'Diet notes (salt, sugar, fat, vegetables and meal pattern)'],
      ['substanceUse', 'Other substance use', 'substance'],
    ],
  },
  {
    key: 'mental',
    title: 'Mental Health',
    visibility: 'mental',
    fields: [
      ['mentalHealthScreen', 'Mental-health screen, score, risk and relevant notes'],
    ],
  },
  {
    key: 'functional',
    title: 'Functional Status',
    visibility: 'functional',
    fields: [
      ['functionalStatus', 'ADLs/IADLs, mobility and assistance required'],
      ['fallsPastYear', 'Falls in the past year'],
    ],
  },
];

function jget(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
}

function stringValue(value) {
  return value == null ? '' : String(value).trim();
}

function numberValue(value) {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function isPlainObject(value) {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function deepMerge(current, incoming) {
  const left = isPlainObject(current) ? current : {};
  if (!isPlainObject(incoming)) return incoming;
  const result = { ...left };
  Object.entries(incoming).forEach(([key, value]) => {
    result[key] = isPlainObject(value) && isPlainObject(result[key])
      ? deepMerge(result[key], value)
      : value;
  });
  return result;
}

function hasMeaningfulValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return Boolean(value.trim());
  if (typeof value === 'number' || typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.some(hasMeaningfulValue);
  if (isPlainObject(value)) return Object.values(value).some(hasMeaningfulValue);
  return false;
}

function toTextValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item;
        if (isPlainObject(item)) return item.name || item.drug || item.label || JSON.stringify(item);
        return String(item ?? '');
      })
      .filter(Boolean)
      .join(', ');
  }
  if (isPlainObject(value)) return JSON.stringify(value);
  return String(value);
}

function toStringArray(value) {
  if (Array.isArray(value)) return value.map(stringValue).filter(Boolean);
  if (typeof value === 'string') {
    return value.split(/[|,\n]/g).map(stringValue).filter(Boolean);
  }
  return [];
}

function normalizeGender(value) {
  const normalized = stringValue(value).toLowerCase();
  if (!normalized) return 'unknown';
  if (['male', 'm', 'man', 'boy'].includes(normalized)) return 'male';
  if (['female', 'f', 'woman', 'girl'].includes(normalized)) return 'female';
  if (normalized.includes('trans')) return 'transgender';
  if (/non.?binary/.test(normalized)) return 'non-binary';
  return normalized;
}

function normalizeAgeGroup(value) {
  const normalized = stringValue(value)
    .toLowerCase()
    .replace(/[_–—-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return '';
  if (/newborn|neonate|0\s*to\s*27|0\s*27/.test(normalized)) return 'newborn';
  if (/young infant|28\s*to\s*59|28\s*59/.test(normalized)) return 'youngInfant';
  if (/infant|2\s*to\s*11|2\s*11/.test(normalized)) return 'infant';
  if (/under\s*5|underfive|1\s*to\s*4/.test(normalized)) return 'under5';
  if (/child|5\s*to\s*9/.test(normalized)) return 'child';
  if (/adolescent|10\s*to\s*19/.test(normalized)) return 'adolescent';
  if (/geriatric|elder|60\+|60\s*plus/.test(normalized)) return 'geriatric';
  if (/adult|20\s*to\s*59/.test(normalized)) return 'adult';
  return '';
}

function deriveGroupFromQuantities({ days, months, years }) {
  if (Number.isFinite(days)) {
    if (days <= 27) return 'newborn';
    if (days <= 59) return 'youngInfant';
  }
  if (Number.isFinite(months)) {
    if (months < 2) return 'youngInfant';
    if (months < 12) return 'infant';
    if (months < 60) return 'under5';
  }
  if (Number.isFinite(years)) {
    if (years < 1) return 'infant';
    if (years < 5) return 'under5';
    if (years < 10) return 'child';
    if (years < 20) return 'adolescent';
    if (years < 60) return 'adult';
    return 'geriatric';
  }
  return 'unknown';
}

function parseReportedAge(rawValue, rawUnit) {
  const rawText = stringValue(rawValue).toLowerCase();
  const match = rawText.match(/(\d+(?:\.\d+)?)/);
  const value = numberValue(rawValue) ?? (match ? Number(match[1]) : null);
  if (!Number.isFinite(value)) return {};
  const combined = `${rawText} ${stringValue(rawUnit).toLowerCase()}`;
  if (/\bday|days|दिन/.test(combined)) {
    return { days: value, months: value / 30.4375, years: value / 365.2425 };
  }
  if (/\bweek|weeks|सप्ताह/.test(combined)) {
    return { days: value * 7, months: (value * 7) / 30.4375, years: (value * 7) / 365.2425 };
  }
  if (/\bmonth|months|माह|महीन/.test(combined)) {
    return { days: value * 30.4375, months: value, years: value / 12 };
  }
  return { days: value * 365.2425, months: value * 12, years: value };
}

function ageFromDob(rawDob, rawAsOn) {
  const dob = rawDob ? new Date(rawDob) : null;
  const asOn = rawAsOn ? new Date(rawAsOn) : new Date();
  if (!dob || Number.isNaN(dob.getTime()) || Number.isNaN(asOn.getTime())) return {};
  const difference = asOn.getTime() - dob.getTime();
  if (difference < 0) return {};
  const days = Math.floor(difference / 86400000);
  return { days, months: days / 30.4375, years: days / 365.2425 };
}

function deriveAgeSnapshot(citizen, visitMeta, visitContext) {
  const explicitGroup = normalizeAgeGroup(
    visitMeta?.ageGroupAtVisit || visitContext?.ageGroupAtVisit || visitContext?.ageGroup || citizen?.ageGroup
  );
  let days = numberValue(visitMeta?.ageInDaysAtVisit ?? visitContext?.ageInDaysAtVisit ?? visitContext?.ageInDays);
  let months = numberValue(
    visitMeta?.ageInMonthsAtVisit ?? visitContext?.ageInMonthsAtVisit ?? visitContext?.ageInMonths
  );
  let years = numberValue(
    visitMeta?.ageYearsAtVisit ?? visitContext?.ageYearsAtVisit ?? visitContext?.ageYears
  );

  if (days === null && months === null && years === null) {
    const fromDob = ageFromDob(
      visitMeta?.dateOfBirthAtVisit || citizen?.dateOfBirth || citizen?.dob,
      visitMeta?.ageAsOnDate || visitMeta?.visitDate || visitMeta?.date
    );
    days = numberValue(fromDob.days);
    months = numberValue(fromDob.months);
    years = numberValue(fromDob.years);
  }

  if (days === null && months === null && years === null) {
    const reported = parseReportedAge(
      visitMeta?.ageValueAtVisit ?? visitMeta?.citizenAge ?? visitContext?.ageValueAtVisit ?? citizen?.age,
      visitMeta?.ageUnitAtVisit || visitContext?.ageUnitAtVisit || citizen?.ageUnit
    );
    days = numberValue(reported.days);
    months = numberValue(reported.months);
    years = numberValue(reported.years);
  }

  if (days === null && Number.isFinite(months)) days = months * 30.4375;
  if (days === null && Number.isFinite(years)) days = years * 365.2425;
  if (months === null && Number.isFinite(days)) months = days / 30.4375;
  if (months === null && Number.isFinite(years)) months = years * 12;
  if (years === null && Number.isFinite(days)) years = days / 365.2425;
  if (years === null && Number.isFinite(months)) years = months / 12;

  return {
    group: explicitGroup || deriveGroupFromQuantities({ days, months, years }),
    days: Number.isFinite(days) ? Math.floor(days) : null,
    months: Number.isFinite(months) ? months : null,
    years: Number.isFinite(years) ? years : null,
    approximate: Boolean(
      visitMeta?.ageApproximateAtVisit ?? visitContext?.ageApproximateAtVisit ?? citizen?.ageIsApproximate
    ),
  };
}

function ageGroupLabel(group) {
  return {
    newborn: 'Newborn (0–27 days)',
    youngInfant: 'Young infant (28–59 days)',
    infant: 'Infant (2–11 months)',
    under5: 'Under-5 (1–4 years)',
    child: 'Child (5–9 years)',
    adolescent: 'Adolescent (10–19 years)',
    adult: 'Adult (20–59 years)',
    geriatric: 'Geriatric (60+ years)',
  }[group] || 'Age group not derived';
}

function formatAgeSnapshot(snapshot) {
  if (!snapshot || snapshot.group === 'unknown') return '';
  const prefix = snapshot.approximate ? 'Approximate age' : 'Age';
  if (['newborn', 'youngInfant'].includes(snapshot.group) && Number.isFinite(snapshot.days)) {
    return `${prefix}: ${snapshot.days} days`;
  }
  if (snapshot.group === 'infant' && Number.isFinite(snapshot.months)) {
    return `${prefix}: ${Math.max(0, Math.floor(snapshot.months))} months`;
  }
  if (Number.isFinite(snapshot.years)) return `${prefix}: ${Math.max(0, Math.floor(snapshot.years))} years`;
  return ageGroupLabel(snapshot.group);
}

function contextBelongs(context, visitId, citizenId) {
  if (!isPlainObject(context)) return false;
  const contextVisitId = stringValue(context.visitId || context.id || context._id);
  const contextCitizenId = stringValue(context.citizenId || context.cid);
  if (contextVisitId && visitId && contextVisitId !== visitId) return false;
  if (contextCitizenId && citizenId && contextCitizenId !== citizenId) return false;
  return true;
}

function normalizeLoadedHistory(payload) {
  const source = isPlainObject(payload) ? payload : {};
  const normalized = deepMerge(initialState, source);
  normalized.pastIllnesses = toStringArray(source.pastIllnesses);
  [
    'personalHistory',
    'familyHistory',
    'menstrualHistory',
    'allergies',
    'surgeries',
    'currentMedications',
    'adverseDrugReactions',
    'immunizationHistory',
    'occupationalExposures',
    'environmentalExposures',
    'fallsPastYear',
  ].forEach((key) => {
    normalized[key] = toTextValue(source[key] ?? normalized[key]);
  });
  normalized.contextual = deepMerge(contextualInitialState, source.contextual || {});
  return normalized;
}

function responseStatus(value) {
  return Number(value?.status || value?.response?.status || value?.data?.status || 0);
}

function responsePayload(response) {
  return response && Object.prototype.hasOwnProperty.call(response, 'data') ? response.data : response;
}

function isSuccessfulResponse(response) {
  const payload = responsePayload(response);
  return Boolean(response) && responseStatus(response) < 400 && response?.ok !== false && payload?.success !== false;
}

function errorMessage(error, fallback) {
  return error?.response?.data?.message || error?.data?.message || error?.message || fallback;
}

async function requestWithFallback(method, urls, body) {
  let lastError = null;
  for (let index = 0; index < urls.length; index += 1) {
    try {
      const response = method === 'get' ? await http.get(urls[index]) : await http.post(urls[index], body);
      if (isSuccessfulResponse(response)) return response;
      const error = new Error(errorMessage(responsePayload(response), method === 'get' ? 'Failed to load' : 'Save failed'));
      error.status = responseStatus(response);
      lastError = error;
      if (![404, 405].includes(error.status) || index === urls.length - 1) throw error;
    } catch (error) {
      lastError = error;
      const status = responseStatus(error);
      if (![404, 405].includes(status) || index === urls.length - 1) throw error;
    }
  }
  throw lastError || new Error(method === 'get' ? 'Failed to load' : 'Save failed');
}

function extractHistory(response) {
  const payload = responsePayload(response) || {};
  return (
    payload?.data?.history ||
    payload?.data?.medicalHistory ||
    payload?.data ||
    payload?.history ||
    payload?.medicalHistory ||
    payload?.visit?.history ||
    payload?.visit?.medicalHistory ||
    {}
  );
}

function extractVisit(response) {
  const payload = responsePayload(response) || {};
  return payload?.visit || payload?.data?.visit || payload?.data || payload || {};
}

function Section({ title, description, children }) {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ mb: description ? 0.5 : 1 }}>{title}</Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{description}</Typography>
      ) : null}
      {children}
    </Box>
  );
}

function FieldGrid({
  fields,
  values,
  onChange,
  readOnly,
  fieldProps,
  showMenstrual,
  showSubstance,
}) {
  const visibleFields = fields.filter(([key, , mode]) => {
    if (mode === 'menstrual') return showMenstrual || hasMeaningfulValue(values[key]);
    if (mode === 'substance' || mode === 'substanceHalf') {
      return showSubstance || hasMeaningfulValue(values[key]);
    }
    return true;
  });

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12,minmax(0,1fr))', gap: 2 }}>
      {visibleFields.map(([key, label, mode]) => (
        <TextField
          key={key}
          label={label}
          value={values[key] ?? ''}
          onChange={(event) => onChange(key, event.target.value)}
          fullWidth
          multiline={!['half', 'substanceHalf'].includes(mode)}
          minRows={['half', 'substanceHalf'].includes(mode) ? undefined : 2}
          sx={{ gridColumn: { xs: 'span 12', sm: ['half', 'substanceHalf'].includes(mode) ? 'span 6' : 'span 12' } }}
          {...fieldProps}
          disabled={readOnly}
        />
      ))}
    </Box>
  );
}
export default function CitizenMedicalHistory({ embedded = false, onSectionSaved, onSectionDirty } = {}) {
  const theme = useTheme();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const query = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);

  const user = jget('loggedInUser') || jget('cmfm_user') || jget('user') || {};
  const role = stringValue(user?.role || user?.userType).toLowerCase();
  const isIntern = role.includes('intern');
  const isCitizenRole = role === 'citizen';

  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');
  const [citizenId, setCitizenId] = useState('');
  const [visitId, setVisitId] = useState('');
  const [citizenSnapshot, setCitizenSnapshot] = useState({});
  const [visitMeta, setVisitMeta] = useState({});
  const [identityMismatch, setIdentityMismatch] = useState(false);
  const [contextWarning, setContextWarning] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const fieldProps = theme.palette.mode === 'dark'
    ? { InputLabelProps: { style: { color: '#fff' } }, inputProps: { style: { color: '#fff' } } }
    : { InputLabelProps: { style: { color: 'inherit' } } };

  const routeCitizenId = useMemo(
    () => stringValue(params.citizenId || query.get('citizenId')),
    [params.citizenId, query]
  );
  const urlVisitId = useMemo(
    () => stringValue(params.visitId || query.get('visitId') || location.state?.visitId),
    [params.visitId, query, location.state]
  );

  useEffect(() => {
    const stateCitizen = location.state?.citizen || location.state?.activeCitizen || {};
    const storedCitizen = jget('activeCitizen') || {};
    const stateCitizenId = stringValue(stateCitizen.citizenId || stateCitizen.id || stateCitizen._id);
    const storedCitizenId = stringValue(storedCitizen.citizenId || storedCitizen.id || storedCitizen._id);
    const resolvedCitizenId = stringValue(
      routeCitizenId ||
        stateCitizenId ||
        localStorage.getItem('activeCitizenId') ||
        localStorage.getItem('currentCitizenId') ||
        localStorage.getItem('citizenId') ||
        storedCitizenId
    );
    const matchingCitizen =
      (stateCitizenId === resolvedCitizenId && stateCitizenId && stateCitizen) ||
      (storedCitizenId === resolvedCitizenId && storedCitizenId && storedCitizen) ||
      { citizenId: resolvedCitizenId };
    const scopedVisit = resolvedCitizenId ? stringValue(localStorage.getItem(`activeVisitId:${resolvedCitizenId}`)) : '';
    const storedVisit = stringValue(localStorage.getItem('activeVisitId') || localStorage.getItem('visitId'));
    const resolvedVisitId = stringValue(urlVisitId || scopedVisit || storedVisit);

    setCitizenId(resolvedCitizenId);
    setVisitId(resolvedVisitId);
    setCitizenSnapshot(matchingCitizen || {});
    setIdentityMismatch(false);
    setContextWarning(
      routeCitizenId && storedCitizenId && routeCitizenId !== storedCitizenId
        ? 'The browser had a different previously selected citizen. Route and Visit identity will be used; stale details were ignored.'
        : ''
    );

    if (resolvedCitizenId && resolvedVisitId) {
      try {
        localStorage.setItem(`activeVisitId:${resolvedCitizenId}`, resolvedVisitId);
        localStorage.setItem('activeVisitId', resolvedVisitId);
        localStorage.setItem('visitId', resolvedVisitId);
      } catch {}
    }
  }, [location.state, routeCitizenId, urlVisitId]);

  const visitContext = useMemo(() => {
    const globalContext = jget('activeVisitContext') || {};
    const scopedContext = visitId ? jget(`activeVisitContext:${visitId}`) || {} : {};
    const stateContext = location.state?.visitContext || location.state?.context || {};
    const safeGlobal = contextBelongs(globalContext, visitId, citizenId) ? globalContext : {};
    const safeScoped = contextBelongs(scopedContext, visitId, citizenId) ? scopedContext : {};
    const safeState = contextBelongs(stateContext, visitId, citizenId) ? stateContext : {};
    return deepMerge(deepMerge(deepMerge(safeGlobal, safeScoped), safeState), visitMeta || {});
  }, [visitId, citizenId, location.state, visitMeta]);

  const ageSnapshot = useMemo(
    () => deriveAgeSnapshot(citizenSnapshot, visitMeta, visitContext),
    [citizenSnapshot, visitMeta, visitContext]
  );
  const citizenGender = useMemo(
    () => normalizeGender(visitMeta?.citizenGender || visitContext?.citizenGender || citizenSnapshot?.gender || citizenSnapshot?.sex),
    [visitMeta, visitContext, citizenSnapshot]
  );

  const visitCategory = stringValue(visitContext?.visitCategory || visitContext?.serviceArea);
  const clinicalPathway = stringValue(visitContext?.clinicalPathway || visitContext?.clinicalFocus);
  const complaintGroup = stringValue(visitContext?.complaintGroup || visitContext?.primaryPresentingProblem);
  const encounterType = stringValue(visitContext?.encounterType || visitContext?.visitType);
  const maternalContext = stringValue(visitContext?.maternalContext).toLowerCase();
  const triagePriority = stringValue(visitContext?.triagePriority).toLowerCase();
  const contextText = `${visitCategory} ${clinicalPathway} ${complaintGroup} ${encounterType}`.toLowerCase();

  const derivedClinicalContext = useMemo(
    () => derivePrimaryClinicalContext({
      serviceArea: stringValue(visitContext?.serviceArea || visitContext?.visitCategory || 'General OPD'),
      chiefComplaints: visitContext?.chiefComplaints || visitMeta?.chiefComplaints || [],
      clinicalPathway: stringValue(visitContext?.clinicalPathway || visitContext?.clinicalFocus),
      triagePriority: stringValue(visitContext?.triagePriority),
    }),
    [visitContext, visitMeta]
  );

  const activeClinicalModules = useMemo(() => {
    const explicit = Array.isArray(visitContext?.activeClinicalModules)
      ? visitContext.activeClinicalModules
      : [];
    const source = explicit.length ? explicit : (derivedClinicalContext?.activeClinicalModules || []);
    return Array.from(
      new Set(
        source
          .map((item) => stringValue(item).toLowerCase())
          .filter(Boolean)
      )
    );
  }, [visitContext, derivedClinicalContext]);

  const historyModulePlan = useMemo(
    () => getSectionModulePlan({ modules: activeClinicalModules, section: 'history' }),
    [activeClinicalModules]
  );

  const activeModuleLabels = useMemo(
    () => moduleLabels(activeClinicalModules),
    [activeClinicalModules]
  );

  const chiefComplaintSummary = useMemo(
    () => formatChiefComplaintSummary(
      visitContext?.chiefComplaints || visitMeta?.chiefComplaints || [],
      visitContext?.chiefComplaintAdditional ||
        visitContext?.additionalChiefComplaint ||
        visitMeta?.chiefComplaintAdditional ||
        visitMeta?.additionalChiefComplaint ||
        ''
    ),
    [visitContext, visitMeta]
  );

  const chiefComplaintItems = useMemo(() => {
    const raw = Array.isArray(visitContext?.chiefComplaints)
      ? visitContext.chiefComplaints
      : Array.isArray(visitMeta?.chiefComplaints)
        ? visitMeta.chiefComplaints
        : [];

    const fallbackComplaint = stringValue(
      visitContext?.presentingComplaint ||
      visitContext?.primaryPresentingProblem ||
      visitMeta?.presentingComplaint ||
      ''
    );
    const source = raw.length ? raw : (fallbackComplaint ? [fallbackComplaint] : []);

    return source
      .map((item, index) => ({
        key: complaintStorageKey(item, index),
        label: complaintDisplayName(item, index),
        duration: complaintDurationText(item),
      }))
      .filter((item) => item.label);
  }, [visitContext, visitMeta]);

  const activeContextSections = useMemo(() => {
    const sections = [];
    const group = ageSnapshot.group;
    if (['newborn', 'youngInfant'].includes(group) || /newborn|neonatal|young infant/.test(contextText)) {
      sections.push('newborn');
    } else if (
      ['infant', 'under5', 'child'].includes(group) ||
      /under.?5|imnci|child health|paediatric|pediatric|malnutrition|growth/.test(contextText)
    ) {
      sections.push('paediatric');
    }
    if (group === 'adolescent' || /adolescent/.test(contextText)) sections.push('adolescent');
    if (['anc', 'pregnant'].includes(maternalContext) || /\banc\b|antenatal|pregnan/.test(contextText)) sections.push('anc');
    if (['pnc', 'postnatal'].includes(maternalContext) || /\bpnc\b|postnatal|postpartum/.test(contextText)) sections.push('pnc');
    if (/ncd|diabetes|hypertension|chronic care|copd|asthma|thyroid/.test(contextText)) sections.push('ncd');
    if (group === 'geriatric' || /geriatric|elderly/.test(contextText)) sections.push('geriatric');
    // Phase 4A.3: let the shared complaint/pathway engine activate the History
    // blocks wherever a reusable clinical module already carries that intent.
    // Existing age/service heuristics remain as fallbacks for legacy Visits.
    if (activeClinicalModules.includes('newborn')) sections.push('newborn');
    if (activeClinicalModules.includes('paediatric')) sections.push('paediatric');
    if (activeClinicalModules.includes('adolescent')) sections.push('adolescent');
    if (activeClinicalModules.includes('ncd')) sections.push('ncd');
    if (activeClinicalModules.includes('geriatric')) sections.push('geriatric');
    if (
      /occupational|workplace|factory|worker|dust|fume|chemical|heat|hearing|back pain|limb pain|dermatitis/.test(contextText) ||
      activeClinicalModules.includes('occupational')
    ) sections.push('occupational');
    if (
      triagePriority === 'emergency' ||
      /emergency|urgent|trauma|chest pain|unconscious|seizure/.test(contextText) ||
      activeClinicalModules.some((key) => ['red-flag', 'injury', 'toxicology', 'heat'].includes(key))
    ) {
      sections.push('emergency');
    }
    return Array.from(new Set(sections));
  }, [ageSnapshot.group, contextText, maternalContext, triagePriority, activeClinicalModules]);

  const visibleContextSections = useMemo(() => {
    const existing = Object.keys(contextSections).filter((key) => hasMeaningfulValue(form.contextual?.[key]));
    return Array.from(new Set([...activeContextSections, ...existing]));
  }, [activeContextSections, form.contextual]);

  const visibility = useMemo(() => {
    const medicationData = hasMeaningfulValue({
      currentMedications: form.currentMedications,
      lastDoseTime: form.lastDoseTime,
      medicationAdherence: form.medicationAdherence,
      adverseDrugReactions: form.adverseDrugReactions,
    });
    const exposureData = hasMeaningfulValue({
      recentTravel: form.recentTravel,
      occupationalExposures: form.occupationalExposures,
      environmentalExposures: form.environmentalExposures,
    });
    const lifestyleData = hasMeaningfulValue({
      tobaccoPackYears: form.tobaccoPackYears,
      alcoholUnitsPerWeek: form.alcoholUnitsPerWeek,
      physicalActivityMinsPerWeek: form.physicalActivityMinsPerWeek,
      dietNotes: form.dietNotes,
      sleepIssues: form.sleepIssues,
      substanceUse: form.substanceUse,
    });
    const functionalData = hasMeaningfulValue({ functionalStatus: form.functionalStatus, fallsPastYear: form.fallsPastYear });

    return {
      medication:
        !['newborn', 'youngInfant'].includes(ageSnapshot.group) ||
        medicationData ||
        /ncd|chronic|tuberculosis|asthma|copd|epilepsy/.test(contextText),
      immunization:
        ['newborn', 'youngInfant', 'infant', 'under5', 'child', 'adolescent'].includes(ageSnapshot.group) ||
        activeContextSections.some((key) => ['anc', 'pnc', 'paediatric', 'newborn'].includes(key)) ||
        hasMeaningfulValue(form.immunizationHistory) ||
        /immuni/.test(contextText),
      exposure:
        !['newborn', 'youngInfant'].includes(ageSnapshot.group) ||
        exposureData ||
        /fever|infection|respiratory|occupational/.test(contextText),
      lifestyle:
        ['adolescent', 'adult', 'geriatric'].includes(ageSnapshot.group) ||
        activeContextSections.some((key) => ['ncd', 'occupational', 'geriatric'].includes(key)) ||
        lifestyleData,
      mental:
        ['adolescent', 'adult', 'geriatric'].includes(ageSnapshot.group) ||
        hasMeaningfulValue(form.mentalHealthScreen),
      functional:
        ageSnapshot.group === 'geriatric' || activeContextSections.includes('geriatric') || functionalData,
      menstrual:
        citizenGender === 'female' ||
        activeContextSections.some((key) => ['adolescent', 'anc', 'pnc'].includes(key)) ||
        hasMeaningfulValue(form.menstrualHistory),
      substance:
        ['adolescent', 'adult', 'geriatric'].includes(ageSnapshot.group) ||
        hasMeaningfulValue({
          tobaccoPackYears: form.tobaccoPackYears,
          alcoholUnitsPerWeek: form.alcoholUnitsPerWeek,
          substanceUse: form.substanceUse,
        }),
    };
  }, [form, ageSnapshot.group, contextText, activeContextSections, citizenGender]);

  const profileSummary = useMemo(() => {
    const parts = [];
    const formattedAge = formatAgeSnapshot(ageSnapshot);
    if (formattedAge) parts.push(formattedAge);
    if (ageSnapshot.group !== 'unknown') parts.push(ageGroupLabel(ageSnapshot.group));
    const gender = stringValue(visitMeta?.citizenGender || citizenSnapshot?.gender || citizenSnapshot?.sex);
    if (gender) parts.push(gender);
    return parts.join(' • ');
  }, [ageSnapshot, citizenSnapshot, visitMeta]);

  const contextChips = useMemo(() => {
    const items = [];
    if (encounterType) items.push(`Encounter: ${encounterType}`);
    if (visitCategory) items.push(`Service Area: ${visitCategory}`);
    if (clinicalPathway) items.push(`Clinical Focus: ${clinicalPathway}`);
    if (complaintGroup) items.push(`Presenting Problem: ${complaintGroup}`);
    if (maternalContext) items.push(`Maternal Context: ${maternalContext.toUpperCase()}`);
    if (triagePriority) items.push(`Triage: ${triagePriority}`);
    return items;
  }, [encounterType, visitCategory, clinicalPathway, complaintGroup, maternalContext, triagePriority]);

  const readOnlyByVisit = useVisitReadOnly(visitId, citizenId);
  const editFlag = (typeof window !== 'undefined' && localStorage.getItem('editReturnedVisit')) || '';
  const allowInternEditReturned = isIntern && visitId && editFlag === visitId;
  const readOnly = isCitizenRole || identityMismatch || (readOnlyByVisit && !allowInternEditReturned);

  useEffect(() => {
    let alive = true;
    async function load() {
      if (!visitId) {
        setLoading(false);
        setLoadError('No active Visit was found. Open History from a selected Visit.');
        return;
      }
      try {
        setLoading(true);
        setLoadError('');
        setSaveError('');
        setIdentityMismatch(false);
        const historyPromise = requestWithFallback('get', [
          `/api/visits/${encodeURIComponent(visitId)}/history`,
          `/api/visit-history/${encodeURIComponent(visitId)}`,
        ]);
        const visitPromise = http.get(`/api/visits/${encodeURIComponent(visitId)}`).catch(() => null);
        const [historyResponse, visitResponse] = await Promise.all([historyPromise, visitPromise]);
        if (!alive) return;

        const loadedHistory = extractHistory(historyResponse);
        const loadedVisit = visitResponse ? extractVisit(visitResponse) : {};
        const loadedCitizenId = stringValue(loadedVisit?.citizenId);

        if (loadedCitizenId && citizenId && loadedCitizenId !== citizenId) {
          setIdentityMismatch(true);
          setLoadError('The selected citizen does not match this Visit. Reopen the correct Visit from Previous Visits.');
        } else if (!citizenId && loadedCitizenId) {
          setCitizenId(loadedCitizenId);
          setCitizenSnapshot((current) => ({
            ...current,
            citizenId: loadedCitizenId,
            name: current?.name || loadedVisit?.citizenName || '',
            gender: current?.gender || loadedVisit?.citizenGender || '',
            age: current?.age || loadedVisit?.citizenAge || '',
          }));
        }

        setVisitMeta(loadedVisit || {});
        setForm(normalizeLoadedHistory(loadedHistory));
      } catch (error) {
        if (alive) setLoadError(errorMessage(error, 'Failed to load medical history'));
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, [visitId, citizenId]);

  const markSectionDirty = useCallback(() => {
    if (typeof onSectionDirty === 'function') onSectionDirty();
  }, [onSectionDirty]);

  const updateCommonField = useCallback((key, value) => {
    markSectionDirty();
    setForm((current) => ({ ...current, [key]: value ?? '' }));
  }, [markSectionDirty]);

  const updateContextField = useCallback((sectionKey, fieldKey, value) => {
    markSectionDirty();
    setForm((current) => ({
      ...current,
      contextual: {
        ...(current.contextual || {}),
        [sectionKey]: {
          ...(current.contextual?.[sectionKey] || {}),
          [fieldKey]: value ?? '',
        },
      },
    }));
  }, [markSectionDirty]);

  const updateComplaintHopi = useCallback((complaint, fieldKey, value) => {
    markSectionDirty();
    setForm((current) => {
      const currentComplaintHopi = isPlainObject(current.contextual?.complaintHopi)
        ? current.contextual.complaintHopi
        : {};
      return {
        ...current,
        contextual: {
          ...(current.contextual || {}),
          complaintHopi: {
            ...currentComplaintHopi,
            [complaint.key]: {
              ...(isPlainObject(currentComplaintHopi[complaint.key]) ? currentComplaintHopi[complaint.key] : {}),
              complaint: complaint.label,
              complaintDuration: complaint.duration,
              [fieldKey]: value ?? '',
            },
          },
        },
      };
    });
  }, [markSectionDirty]);

  const hospitalisations = Array.isArray(form.contextual?.hospitalisations)
    ? form.contextual.hospitalisations
    : [];

  const addHospitalisation = useCallback(() => {
    markSectionDirty();
    setForm((current) => {
      const currentItems = Array.isArray(current.contextual?.hospitalisations)
        ? current.contextual.hospitalisations
        : [];
      return {
        ...current,
        contextual: {
          ...(current.contextual || {}),
          hospitalisations: [...currentItems, emptyHospitalisation()],
        },
      };
    });
  }, [markSectionDirty]);

  const updateHospitalisation = useCallback((index, fieldKey, value) => {
    markSectionDirty();
    setForm((current) => {
      const currentItems = Array.isArray(current.contextual?.hospitalisations)
        ? current.contextual.hospitalisations
        : [];
      const nextItems = currentItems.map((item, itemIndex) => (
        itemIndex === index ? { ...(isPlainObject(item) ? item : {}), [fieldKey]: value ?? '' } : item
      ));
      return {
        ...current,
        contextual: {
          ...(current.contextual || {}),
          hospitalisations: nextItems,
        },
      };
    });
  }, [markSectionDirty]);

  const removeHospitalisation = useCallback((index) => {
    markSectionDirty();
    setForm((current) => {
      const currentItems = Array.isArray(current.contextual?.hospitalisations)
        ? current.contextual.hospitalisations
        : [];
      return {
        ...current,
        contextual: {
          ...(current.contextual || {}),
          hospitalisations: currentItems.filter((_item, itemIndex) => itemIndex !== index),
        },
      };
    });
  }, [markSectionDirty]);

  const requiredMissing = useMemo(() => {
    const missing = [];
    if (!stringValue(form.hopi) && !hasMeaningfulValue(form.contextual?.complaintHopi)) {
      missing.push('History of Present Illness');
    }
    if (activeContextSections.includes('emergency')) {
      const emergency = form.contextual?.emergency || {};
      if (!hasMeaningfulValue(emergency.onsetTimeline) && !hasMeaningfulValue(emergency.dangerSigns)) {
        missing.push('Emergency onset or immediate danger signs');
      }
    }
    if (activeContextSections.includes('newborn')) {
      const newborn = form.contextual?.newborn || {};
      if (!hasMeaningfulValue(newborn.birthHistory) && !hasMeaningfulValue(newborn.feeding)) {
        missing.push('Newborn birth or feeding history');
      }
    }
    if (activeContextSections.includes('anc')) {
      const anc = form.contextual?.anc || {};
      if (!hasMeaningfulValue(anc.gestationalAge) && !hasMeaningfulValue(anc.lmpEdd)) {
        missing.push('Gestational age or LMP/EDD');
      }
    }
    if (activeContextSections.includes('pnc')) {
      const pnc = form.contextual?.pnc || {};
      if (!hasMeaningfulValue(pnc.deliveryDatePlace) && !hasMeaningfulValue(pnc.postpartumDay)) {
        missing.push('Delivery date/place or postpartum day');
      }
    }
    return missing;
  }, [form, activeContextSections]);

  const hasData = useMemo(() => hasMeaningfulValue(form), [form]);
  const canContinue = !readOnly && !saving && !identityMismatch && requiredMissing.length === 0;

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('encounter:can-continue', {
        detail: {
          visitId,
          can: canContinue,
          canContinue,
          canSave: !readOnly && hasData && !saving && !identityMismatch,
          missing: requiredMissing,
        },
      })
    );
  }, [visitId, canContinue, readOnly, hasData, saving, identityMismatch, requiredMissing]);

  const setBusy = useCallback(
    (busy) => window.dispatchEvent(new CustomEvent('encounter:busy', { detail: { visitId, busy } })),
    [visitId]
  );

  const saveHistory = useCallback(
    async ({ requireComplete = false } = {}) => {
      if (readOnly) return false;
      if (!visitId || !citizenId) {
        setSaveError('Missing Visit or citizen identity. Reopen this Visit from Previous Visits.');
        return false;
      }
      if (identityMismatch) {
        setSaveError('Citizen and Visit identities do not match. Saving is blocked.');
        return false;
      }
      if (requireComplete && requiredMissing.length) {
        setSaveError(`Complete before continuing: ${requiredMissing.join('; ')}.`);
        return false;
      }

      try {
        setSaveSuccess('');
        setSaveError('');
        setSaving(true);
        setBusy(true);
        const noneSelected = Array.isArray(form.pastIllnesses) && form.pastIllnesses.includes(NONE_OPTION);
        const history = {
          ...form,
          pastIllnesses: noneSelected ? [NONE_OPTION] : toStringArray(form.pastIllnesses),
          contextual: deepMerge(contextualInitialState, form.contextual || {}),
        };
        const saveResponse = await requestWithFallback(
          'post',
          [
            `/api/visits/${encodeURIComponent(visitId)}/history`,
            `/api/visit-history/${encodeURIComponent(visitId)}`,
          ],
          { citizenId, history }
        );
        setSaveSuccess('Medical history saved');

        const savedVisit = extractVisit(saveResponse);
        const detail = {
          section: 'history',
          visitId,
          citizenId,
          sectionProgress: savedVisit?.sectionProgress?.history || null,
        };
        try {
          window.dispatchEvent(new CustomEvent('encounter:section-saved', { detail }));
        } catch {}
        if (typeof onSectionSaved === 'function') onSectionSaved(detail);

        return true;
      } catch (error) {
        setSaveError(errorMessage(error, 'Save failed'));
        return false;
      } finally {
        setSaving(false);
        setBusy(false);
      }
    },
    [readOnly, visitId, citizenId, identityMismatch, requiredMissing, form, setBusy, onSectionSaved]
  );

  useEffect(() => {
    const onSave = async (event) => {
      const detail = event?.detail || {};
      if (detail.visitId && detail.visitId !== visitId) return;
      const saveAndContinue = detail.intent === 'save-continue';
      const saved = await saveHistory({ requireComplete: saveAndContinue });
      if (!saved || !saveAndContinue) return;
      const resolvedCitizenId = stringValue(
        citizenId ||
          params.citizenId ||
          query.get('citizenId') ||
          citizenSnapshot?.citizenId ||
          localStorage.getItem('activeCitizenId') ||
          localStorage.getItem('citizenId')
      );
      const nextPath = resolvedCitizenId
        ? `/citizen/${encodeURIComponent(resolvedCitizenId)}/encounter/examination?visitId=${encodeURIComponent(visitId)}`
        : `/citizen/encounter/examination?visitId=${encodeURIComponent(visitId)}`;
      navigate(tenantUrl(nextPath));
    };
    window.addEventListener('encounter:save', onSave);
    return () => window.removeEventListener('encounter:save', onSave);
  }, [visitId, citizenId, params.citizenId, query, citizenSnapshot, saveHistory, navigate]);

  return (
    <div className={embedded
      ? "bg-white dark:bg-[#181a1b] text-black dark:text-white"
      : "p-6 bg-white dark:bg-[#181a1b] min-h-screen text-black dark:text-white"
    }>
      {!embedded ? <EncounterHeader visitId={visitId} /> : null}

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
          <CircularProgress size={26} />
          <Typography variant="body2">Loading medical history…</Typography>
        </Box>
      ) : (
        <>
          {loadError ? <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert> : null}
          {contextWarning ? <Alert severity="warning" sx={{ mb: 2 }}>{contextWarning}</Alert> : null}
          {saveError ? <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert> : null}
          {saveSuccess ? <Alert severity="success" sx={{ mb: 2 }}>{saveSuccess}</Alert> : null}
          {saving ? <Alert severity="info" sx={{ mb: 2 }}>Saving medical history…</Alert> : null}

          <Typography variant="h5" sx={{ mb: 2 }}>Medical History</Typography>

          {citizenId ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {citizenSnapshot?.name || visitMeta?.citizenName || 'Selected citizen'}
              {` • ${citizenId}`}
              {profileSummary ? ` • ${profileSummary}` : ''}
            </Alert>
          ) : null}

          {contextChips.length ? (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {contextChips.map((item) => (
                <Chip
                  key={item}
                  size="small"
                  color={item.startsWith('Triage: emergency') ? 'error' : 'primary'}
                  variant="outlined"
                  label={item}
                />
              ))}
            </Box>
          ) : null}

          <Alert severity="info" sx={{ mb: 2 }}>
            Clinical information is stored once in this Visit. Relevant age-, pathway- and complaint-specific blocks appear below and can be reused by specialised clinical views without re-entry.
          </Alert>

          {chiefComplaintSummary ? (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" component="div">Chief complaints for this Visit</Typography>
              <Typography variant="body2">{chiefComplaintSummary}</Typography>
            </Alert>
          ) : null}

          {historyModulePlan.prompts.length ? (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
                History focus from the active clinical context
              </Typography>
              {activeModuleLabels.length ? (
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                  {activeModuleLabels.map((item) => (
                    <Chip key={item.key} size="small" variant="outlined" label={item.label} />
                  ))}
                </Box>
              ) : null}
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {historyModulePlan.prompts.map((prompt) => (
                  <Typography component="li" variant="body2" key={prompt} sx={{ mb: 0.25 }}>
                    {prompt}
                  </Typography>
                ))}
              </Box>
            </Alert>
          ) : null}

          {!readOnly && requiredMissing.length ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {embedded ? 'History can be saved as a draft. Complete before final Visit submission: ' : 'Save is allowed as a draft. Save & Continue requires: '}{requiredMissing.join('; ')}.
            </Alert>
          ) : null}
          {readOnly ? (
            <Alert severity="info" sx={{ mb: 2 }}>This History is read-only for the current user or Visit status.</Alert>
          ) : null}

          <Section
            title="History of Present Illness"
            description="Elaborate each chief complaint with structured chronology and clinical details. The narrative HOPI remains available for information that does not fit the structured fields."
          >
            {chiefComplaintItems.length ? (
              <Box sx={{ display: 'grid', gap: 2, mb: 2 }}>
                {chiefComplaintItems.map((complaint) => {
                  const details = isPlainObject(form.contextual?.complaintHopi?.[complaint.key])
                    ? form.contextual.complaintHopi[complaint.key]
                    : {};
                  return (
                    <Box
                      key={complaint.key}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 2,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{complaint.label}</Typography>
                        {complaint.duration ? <Chip size="small" variant="outlined" label={complaint.duration} /> : null}
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12,minmax(0,1fr))', gap: 2 }}>
                        <TextField
                          select
                          SelectProps={{ native: true }}
                          label="Onset"
                          value={details.onset || ''}
                          onChange={(event) => updateComplaintHopi(complaint, 'onset', event.target.value)}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        >
                          {HOPI_ONSET_OPTIONS.map((option) => (
                            <option key={option || 'blank'} value={option}>{option || 'Select'}</option>
                          ))}
                        </TextField>
                        <TextField
                          select
                          SelectProps={{ native: true }}
                          label="Course / progression"
                          value={details.course || ''}
                          onChange={(event) => updateComplaintHopi(complaint, 'course', event.target.value)}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        >
                          {HOPI_COURSE_OPTIONS.map((option) => (
                            <option key={option || 'blank'} value={option}>{option || 'Select'}</option>
                          ))}
                        </TextField>
                        <TextField
                          select
                          SelectProps={{ native: true }}
                          label="Severity"
                          value={details.severity || ''}
                          onChange={(event) => updateComplaintHopi(complaint, 'severity', event.target.value)}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        >
                          {HOPI_SEVERITY_OPTIONS.map((option) => (
                            <option key={option || 'blank'} value={option}>{option || 'Select'}</option>
                          ))}
                        </TextField>
                        <TextField
                          label="Pattern / timing"
                          value={details.pattern || ''}
                          onChange={(event) => updateComplaintHopi(complaint, 'pattern', event.target.value)}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        />
                        <TextField
                          label="Previous similar episodes"
                          value={details.similarEpisodes || ''}
                          onChange={(event) => updateComplaintHopi(complaint, 'similarEpisodes', event.target.value)}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        />
                        <TextField
                          label="Aggravating factors"
                          value={details.aggravatingFactors || ''}
                          onChange={(event) => updateComplaintHopi(complaint, 'aggravatingFactors', event.target.value)}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        />
                        <TextField
                          label="Relieving factors"
                          value={details.relievingFactors || ''}
                          onChange={(event) => updateComplaintHopi(complaint, 'relievingFactors', event.target.value)}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        />
                        <TextField
                          label="Associated symptoms"
                          value={details.associatedSymptoms || ''}
                          onChange={(event) => updateComplaintHopi(complaint, 'associatedSymptoms', event.target.value)}
                          multiline
                          minRows={2}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        />
                        <TextField
                          label="Relevant negatives"
                          value={details.relevantNegatives || ''}
                          onChange={(event) => updateComplaintHopi(complaint, 'relevantNegatives', event.target.value)}
                          multiline
                          minRows={2}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        />
                        <TextField
                          label="Treatment already taken and response"
                          value={details.treatmentTaken || ''}
                          onChange={(event) => updateComplaintHopi(complaint, 'treatmentTaken', event.target.value)}
                          multiline
                          minRows={2}
                          sx={{ gridColumn: 'span 12' }}
                          {...fieldProps}
                          disabled={readOnly}
                        />
                        <TextField
                          label="Complaint-specific notes"
                          value={details.notes || ''}
                          onChange={(event) => updateComplaintHopi(complaint, 'notes', event.target.value)}
                          multiline
                          minRows={2}
                          sx={{ gridColumn: 'span 12' }}
                          {...fieldProps}
                          disabled={readOnly}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : null}

            <TextField
              label="HOPI narrative / additional details"
              value={form.hopi || ''}
              onChange={(event) => updateCommonField('hopi', event.target.value)}
              fullWidth
              multiline
              minRows={4}
              {...fieldProps}
              disabled={readOnly}
            />
          </Section>

          <Section
            title="Recent / Intervening Hospitalisation or Major Healthcare Event"
            description="Use for a new Visit when a recent/relevant hospitalisation matters, and for follow-up Visits when hospitalisation or major care occurred since the previous CMFM Visit. This does not alter the earlier CMFM Visit."
          >
            {hospitalisations.length ? (
              <Box sx={{ display: 'grid', gap: 2 }}>
                {hospitalisations.map((item, index) => {
                  const hospitalisation = isPlainObject(item) ? item : {};
                  return (
                    <Box
                      key={`hospitalisation-${index}`}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 2,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          Hospitalisation / major healthcare event {index + 1}
                        </Typography>
                        {!readOnly ? (
                          <Button
                            type="button"
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => removeHospitalisation(index)}
                          >
                            Remove
                          </Button>
                        ) : null}
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12,minmax(0,1fr))', gap: 2 }}>
                        <TextField
                          type="date"
                          label="Admission date"
                          value={hospitalisation.admissionDate || ''}
                          onChange={(event) => updateHospitalisation(index, 'admissionDate', event.target.value)}
                          InputLabelProps={{ ...(fieldProps.InputLabelProps || {}), shrink: true }}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                          disabled={readOnly}
                        />
                        <TextField
                          type="date"
                          label="Discharge date"
                          value={hospitalisation.dischargeDate || ''}
                          onChange={(event) => updateHospitalisation(index, 'dischargeDate', event.target.value)}
                          InputLabelProps={{ ...(fieldProps.InputLabelProps || {}), shrink: true }}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                          disabled={readOnly}
                        />
                        <TextField
                          select
                          SelectProps={{ native: true }}
                          label="Facility type"
                          value={hospitalisation.facilityType || ''}
                          onChange={(event) => updateHospitalisation(index, 'facilityType', event.target.value)}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        >
                          {FACILITY_TYPE_OPTIONS.map((option) => (
                            <option key={option || 'blank'} value={option}>{option || 'Select'}</option>
                          ))}
                        </TextField>
                        <TextField
                          select
                          SelectProps={{ native: true }}
                          label="ICU admission"
                          value={hospitalisation.icuAdmission || ''}
                          onChange={(event) => updateHospitalisation(index, 'icuAdmission', event.target.value)}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        >
                          {ICU_OPTIONS.map((option) => (
                            <option key={option || 'blank'} value={option}>{option || 'Select'}</option>
                          ))}
                        </TextField>
                        <TextField
                          label="Facility / hospital name"
                          value={hospitalisation.facilityName || ''}
                          onChange={(event) => updateHospitalisation(index, 'facilityName', event.target.value)}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        />
                        <TextField
                          label="Location / city"
                          value={hospitalisation.location || ''}
                          onChange={(event) => updateHospitalisation(index, 'location', event.target.value)}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        />
                        <TextField
                          label="Reason for admission / major healthcare event"
                          value={hospitalisation.reason || ''}
                          onChange={(event) => updateHospitalisation(index, 'reason', event.target.value)}
                          multiline
                          minRows={2}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        />
                        <TextField
                          label="Discharge / final diagnosis"
                          value={hospitalisation.dischargeDiagnosis || ''}
                          onChange={(event) => updateHospitalisation(index, 'dischargeDiagnosis', event.target.value)}
                          multiline
                          minRows={2}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        />
                        <TextField
                          label="Procedure / surgery / important treatment"
                          value={hospitalisation.procedures || ''}
                          onChange={(event) => updateHospitalisation(index, 'procedures', event.target.value)}
                          multiline
                          minRows={2}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        />
                        <TextField
                          label="Important investigations / results"
                          value={hospitalisation.importantInvestigations || ''}
                          onChange={(event) => updateHospitalisation(index, 'importantInvestigations', event.target.value)}
                          multiline
                          minRows={2}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        />
                        <TextField
                          label="Medicines / instructions at discharge"
                          value={hospitalisation.dischargeMedicines || ''}
                          onChange={(event) => updateHospitalisation(index, 'dischargeMedicines', event.target.value)}
                          multiline
                          minRows={2}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        />
                        <TextField
                          label="Outcome / current status"
                          value={hospitalisation.outcome || ''}
                          onChange={(event) => updateHospitalisation(index, 'outcome', event.target.value)}
                          multiline
                          minRows={2}
                          sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                          {...fieldProps}
                          disabled={readOnly}
                        />
                        <TextField
                          select
                          SelectProps={{ native: true }}
                          label="Source of information"
                          value={hospitalisation.informationSource || ''}
                          onChange={(event) => updateHospitalisation(index, 'informationSource', event.target.value)}
                          sx={{ gridColumn: 'span 12' }}
                          {...fieldProps}
                          disabled={readOnly}
                        >
                          {INFORMATION_SOURCE_OPTIONS.map((option) => (
                            <option key={option || 'blank'} value={option}>{option || 'Select'}</option>
                          ))}
                        </TextField>
                        <TextField
                          label="Additional notes"
                          value={hospitalisation.notes || ''}
                          onChange={(event) => updateHospitalisation(index, 'notes', event.target.value)}
                          multiline
                          minRows={2}
                          sx={{ gridColumn: 'span 12' }}
                          {...fieldProps}
                          disabled={readOnly}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No recent/intervening hospitalisation or major healthcare event recorded in this Visit.
              </Typography>
            )}
            {!readOnly ? (
              <Button type="button" variant="outlined" sx={{ mt: 1.5 }} onClick={addHospitalisation}>
                Add hospitalisation / healthcare event
              </Button>
            ) : null}
          </Section>

          {visibleContextSections.map((sectionKey) => {
            const definition = contextSections[sectionKey];
            return (
              <Section key={sectionKey} title={definition.title} description={definition.description}>
                <FieldGrid
                  fields={definition.fields}
                  values={form.contextual?.[sectionKey] || {}}
                  onChange={(key, value) => updateContextField(sectionKey, key, value)}
                  readOnly={readOnly}
                  fieldProps={fieldProps}
                  showMenstrual
                  showSubstance
                />
              </Section>
            );
          })}

          {visibleContextSections.length ? <Divider sx={{ my: 3 }} /> : null}

          <Section title="Past Illnesses">
            <Autocomplete
              multiple
              freeSolo
              options={pastIllnessOptions}
              value={Array.isArray(form.pastIllnesses) ? form.pastIllnesses : []}
              onChange={(_event, newValue) => {
                let values = Array.from(new Set((newValue || []).map(stringValue).filter(Boolean)));
                if (values.includes(NONE_OPTION)) values = [NONE_OPTION];
                markSectionDirty();
                setForm((current) => ({ ...current, pastIllnesses: values }));
              }}
              openOnFocus
              renderInput={(autocompleteParams) => {
                const mergedInputLabelProps = {
                  ...(autocompleteParams.InputLabelProps || {}),
                  ...(fieldProps.InputLabelProps || {}),
                  style: {
                    ...(autocompleteParams.InputLabelProps?.style || {}),
                    ...(fieldProps.InputLabelProps?.style || {}),
                  },
                };
                const mergedInputProps = {
                  ...(autocompleteParams.inputProps || {}),
                  ...(fieldProps.inputProps || {}),
                  style: {
                    ...(autocompleteParams.inputProps?.style || {}),
                    ...(fieldProps.inputProps?.style || {}),
                  },
                };
                return (
                  <TextField
                    {...autocompleteParams}
                    label="Select or type illnesses"
                    InputLabelProps={mergedInputLabelProps}
                    inputProps={mergedInputProps}
                    helperText="Choose from the list or type an illness and press Enter."
                    disabled={readOnly}
                  />
                );
              }}
              disabled={readOnly}
            />
          </Section>

          {commonSections.map((section) => {
            if (section.visibility && !visibility[section.visibility]) return null;
            return (
              <Section key={section.key} title={section.title}>
                <FieldGrid
                  fields={section.fields}
                  values={form}
                  onChange={updateCommonField}
                  readOnly={readOnly}
                  fieldProps={fieldProps}
                  showMenstrual={visibility.menstrual}
                  showSubstance={visibility.substance}
                />
              </Section>
            );
          })}
        </>
      )}
    </div>
  );
}
